/**
 * Integrations Settings Page
 *
 * Configures external integrations like Asana sync and Google Calendar.
 */

import { useState, useEffect } from 'react';
import {
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Link2,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowLeftRight,
  Calendar,
  ExternalLink,
  Power,
  Mic,
  FileAudio,
  FileText,
  FileSpreadsheet,
  Upload,
} from 'lucide-react';
import { Card, CardContent, Button } from '@/components/ui';
import {
  useAsanaConfig,
  useAsanaSyncStatus,
  useAsanaWorkspaces,
  useAsanaProjects,
  useAsanaConfigMutations,
  useGoogleCalendarConfig,
  useGoogleCalendarSyncStatus,
  useGoogleCalendars,
  useGoogleCalendarMutations,
  useGitHubJournalConfig,
  useGitHubJournalSyncStatus,
  useGitHubJournalMutations,
  useSpreadsheetConfigs,
  useSpreadsheetStatus,
  useSpreadsheetImportMutations,
  useGitHubSyncStatus,
  useGitHubSyncMutations,
  useGitHubConflictReport,
  useGitHubDeltaReport,
} from '@/hooks/use-integrations';
import type { JournalFormat, GitHubSyncDirection, SpreadsheetImportConfig } from '@/api/integrations';

// Asana logo as inline SVG component
function AsanaLogo({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M18.78 12.653c-2.019 0-3.655 1.636-3.655 3.655s1.636 3.655 3.655 3.655 3.655-1.636 3.655-3.655-1.636-3.655-3.655-3.655zm-13.56 0c-2.019 0-3.655 1.636-3.655 3.655s1.636 3.655 3.655 3.655 3.655-1.636 3.655-3.655-1.636-3.655-3.655-3.655zM12 4.037c-2.019 0-3.655 1.636-3.655 3.655S9.981 11.347 12 11.347s3.655-1.636 3.655-3.655S14.019 4.037 12 4.037z" />
    </svg>
  );
}

interface ProjectMappingRowProps {
  lifeguideProject: string;
  asanaProjectGid: string;
  asanaProjectName?: string;
  sections: Record<string, string>;
  onDelete: () => void;
  isDeleting: boolean;
}

function ProjectMappingRow({
  lifeguideProject,
  asanaProjectGid,
  asanaProjectName,
  sections,
  onDelete,
  isDeleting,
}: ProjectMappingRowProps) {
  const [expanded, setExpanded] = useState(false);
  const sectionCount = Object.keys(sections).length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <ArrowLeftRight className="w-4 h-4 text-gray-400" />
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{lifeguideProject}</span>
            <span className="text-gray-400">↔</span>
            <span className="flex items-center gap-1.5">
              <AsanaLogo className="w-4 h-4 text-[#F06A6A]" />
              <span className="text-gray-700">
                {asanaProjectName || (
                  <span className="font-mono text-xs text-gray-500">{asanaProjectGid}</span>
                )}
              </span>
            </span>
          </div>
          {sectionCount > 0 && (
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
              {sectionCount} section{sectionCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            isLoading={isDeleting}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && sectionCount > 0 && (
        <div className="p-3 border-t border-gray-100 bg-white">
          <p className="text-xs text-gray-500 mb-2">Section Mappings:</p>
          <div className="space-y-1">
            {Object.entries(sections).map(([lgSection, asanaGid]) => (
              <div key={lgSection} className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">{lgSection}</span>
                <span className="text-gray-400">→</span>
                <span className="font-mono text-xs text-gray-500">{asanaGid}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface AddMappingDialogProps {
  workspaceGid: string | null;
  onAdd: (lifeguideProject: string, asanaProjectGid: string) => void;
  onCancel: () => void;
  isAdding: boolean;
}

function AddMappingDialog({ workspaceGid, onAdd, onCancel, isAdding }: AddMappingDialogProps) {
  const [lifeguideProject, setLifeguideProject] = useState('');
  const [selectedProjectGid, setSelectedProjectGid] = useState('');
  const { data: projects, isLoading: loadingProjects } = useAsanaProjects(workspaceGid, true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lifeguideProject && selectedProjectGid) {
      onAdd(lifeguideProject, selectedProjectGid);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Project Mapping</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LifeGuide Topic Path
            </label>
            <input
              type="text"
              value={lifeguideProject}
              onChange={(e) => setLifeguideProject(e.target.value)}
              placeholder="e.g., Work or Personal/Health"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              The top-level topic path to map to Asana
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asana Project
            </label>
            {loadingProjects ? (
              <div className="flex items-center gap-2 text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading projects...
              </div>
            ) : (
              <select
                value={selectedProjectGid}
                onChange={(e) => setSelectedProjectGid(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                <option value="">Select a project...</option>
                {projects?.map((project) => (
                  <option key={project.gid} value={project.gid}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!lifeguideProject || !selectedProjectGid}
              isLoading={isAdding}
            >
              Add Mapping
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// GitHub Logo as inline SVG component
function GitHubLogo({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

// GitHub Journal Integration Component (LogSeq/Obsidian)
function GitHubJournalIntegration() {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const { data: config, isLoading: loadingConfig } = useGitHubJournalConfig();
  const { data: status, isLoading: loadingStatus } = useGitHubJournalSyncStatus();

  const { updateConfig, testConnection, triggerSync, disconnect } =
    useGitHubJournalMutations();

  const handleToggleSync = async (enabled: boolean) => {
    await updateConfig.mutateAsync({ enabled });
  };

  const handleFormatChange = async (journalFormat: JournalFormat) => {
    await updateConfig.mutateAsync({ journalFormat });
  };

  const handleDirectionChange = async (syncDirection: GitHubSyncDirection) => {
    await updateConfig.mutateAsync({ syncDirection });
  };

  const handleJournalPathChange = async (journalPath: string) => {
    await updateConfig.mutateAsync({ journalPath });
  };

  const handleBranchChange = async (branch: string) => {
    await updateConfig.mutateAsync({ branch });
  };

  const handleDisconnect = async () => {
    if (confirm('Are you sure you want to disconnect GitHub Journal sync? This will remove your stored token.')) {
      await disconnect.mutateAsync();
    }
  };

  const handleTriggerSync = async () => {
    await triggerSync.mutateAsync('both');
  };

  const handleTestConnection = async () => {
    await testConnection.mutateAsync();
  };

  if (loadingConfig) {
    return (
      <Card className="mb-6">
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-coral-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6">
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-900 rounded-lg">
                <GitHubLogo className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">GitHub Journal</h2>
                <p className="text-sm text-gray-500">Sync LogSeq/Obsidian journals via GitHub</p>
              </div>
            </div>

            {/* Status indicator */}
            {config?.connected && (
              <div className="flex items-center gap-2">
                {loadingStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : status?.status === 'ok' ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Error</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {!config?.connected ? (
            // Not connected - show connect button
            <div className="text-center py-6">
              <GitHubLogo className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Connect your GitHub repository to sync journal entries with LogSeq or Obsidian.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Requires a GitHub Personal Access Token with repository access.
              </p>
              <Button
                onClick={() => setShowSetupDialog(true)}
                leftIcon={<ExternalLink className="w-4 h-4" />}
              >
                Connect GitHub Repository
              </Button>
            </div>
          ) : (
            <>
              {/* Connected repository info */}
              <div className="py-3 border-b border-gray-100">
                <p className="text-sm text-gray-600">
                  Repository: <span className="font-medium font-mono">{config.repository}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Branch: {config.branch} • Path: {config.journalPath}
                </p>
              </div>

              {/* Enable toggle */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Enable Journal Sync</p>
                  <p className="text-sm text-gray-500">
                    Synchronize journal entries with GitHub
                  </p>
                </div>
                <button
                  onClick={() => handleToggleSync(!config?.enabled)}
                  disabled={updateConfig.isPending}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    config?.enabled ? 'bg-coral-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      config?.enabled ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>

              {config?.enabled && (
                <>
                  {/* Journal Format */}
                  <div className="py-3 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Journal Format
                    </label>
                    <select
                      value={config?.journalFormat || 'logseq'}
                      onChange={(e) => handleFormatChange(e.target.value as JournalFormat)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                      disabled={updateConfig.isPending}
                    >
                      <option value="logseq">LogSeq (YYYY_MM_DD.md)</option>
                      <option value="obsidian">Obsidian (YYYY-MM-DD.md)</option>
                    </select>
                  </div>

                  {/* Branch */}
                  <div className="py-3 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch
                    </label>
                    <input
                      type="text"
                      value={config?.branch || 'main'}
                      onChange={(e) => handleBranchChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                      disabled={updateConfig.isPending}
                      placeholder="main"
                    />
                  </div>

                  {/* Journal Path */}
                  <div className="py-3 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Journal Path
                    </label>
                    <input
                      type="text"
                      value={config?.journalPath || 'journals'}
                      onChange={(e) => handleJournalPathChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                      disabled={updateConfig.isPending}
                      placeholder="journals"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Path to journal files in your repository
                    </p>
                  </div>

                  {/* Sync Direction */}
                  <div className="py-3 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sync Direction
                    </label>
                    <select
                      value={config?.syncDirection || 'bidirectional'}
                      onChange={(e) =>
                        handleDirectionChange(e.target.value as GitHubSyncDirection)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                      disabled={updateConfig.isPending}
                    >
                      <option value="bidirectional">Bidirectional (GitHub ↔ LifeGuide)</option>
                      <option value="inbound">GitHub → LifeGuide only</option>
                      <option value="outbound">LifeGuide → GitHub only</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Bidirectional: Journal entries sync both ways between GitHub and LifeGuide.
                    </p>
                  </div>

                  {/* Sync Status & Actions */}
                  <div className="pt-3">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{status?.syncedFiles || 0}</span> files synced
                        </p>
                        {config?.lastSync && (
                          <p className="text-xs text-gray-400">
                            Last sync: {new Date(config.lastSync).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleTestConnection}
                        isLoading={testConnection.isPending}
                        leftIcon={<Link2 className="w-4 h-4" />}
                      >
                        Test Connection
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleTriggerSync}
                        isLoading={triggerSync.isPending}
                        leftIcon={<RefreshCw className="w-4 h-4" />}
                      >
                        Sync Now
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDisconnect}
                        isLoading={disconnect.isPending}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        leftIcon={<Power className="w-4 h-4" />}
                      >
                        Disconnect
                      </Button>
                    </div>

                    {testConnection.isSuccess && (
                      <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Connection successful</span>
                        </div>
                        {testConnection.data?.journalFilesFound !== undefined && (
                          <p className="text-xs text-green-600 mt-1">
                            Found {testConnection.data.journalFilesFound} journal files in {testConnection.data.journalPath}
                          </p>
                        )}
                      </div>
                    )}

                    {testConnection.isError && (
                      <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Connection failed</span>
                        </div>
                      </div>
                    )}

                    {triggerSync.isSuccess && (
                      <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <div className="flex items-center gap-2 text-blue-700">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Sync completed</span>
                        </div>
                        {triggerSync.data?.results?.inbound && (
                          <p className="text-xs text-blue-600 mt-1">
                            Inbound: {triggerSync.data.results.inbound.files_synced || 0} files synced
                          </p>
                        )}
                        {triggerSync.data?.results?.outbound && (
                          <p className="text-xs text-blue-600 mt-1">
                            Outbound: {triggerSync.data.results.outbound.files_synced || 0} files synced
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* GitHub Setup Dialog */}
      {showSetupDialog && (
        <GitHubSetupDialog
          onSave={async (repository, token, branch, journalPath, journalFormat) => {
            await updateConfig.mutateAsync({
              repository,
              githubToken: token,
              branch,
              journalPath,
              journalFormat,
              enabled: true,
            });
            setShowSetupDialog(false);
          }}
          onCancel={() => setShowSetupDialog(false)}
          isSaving={updateConfig.isPending}
        />
      )}
    </>
  );
}

// GitHub Setup Dialog Component
interface GitHubSetupDialogProps {
  onSave: (repository: string, token: string, branch: string, journalPath: string, journalFormat: JournalFormat) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function GitHubSetupDialog({ onSave, onCancel, isSaving }: GitHubSetupDialogProps) {
  const [repository, setRepository] = useState('');
  const [token, setToken] = useState('');
  const [branch, setBranch] = useState('main');
  const [journalPath, setJournalPath] = useState('journals');
  const [journalFormat, setJournalFormat] = useState<JournalFormat>('logseq');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (repository && token) {
      onSave(repository, token, branch, journalPath, journalFormat);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gray-900 rounded-lg">
            <GitHubLogo className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Connect GitHub Repository</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Repository
            </label>
            <input
              type="text"
              value={repository}
              onChange={(e) => setRepository(e.target.value)}
              placeholder="owner/repository"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Format: username/repository-name
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Personal Access Token
            </label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="ghp_xxxxxxxxxxxx"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Create a token at{' '}
              <a
                href="https://github.com/settings/tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="text-coral-600 hover:underline"
              >
                GitHub Settings → Tokens
              </a>{' '}
              with repo access
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch
            </label>
            <input
              type="text"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              placeholder="main"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Journal Path
            </label>
            <input
              type="text"
              value={journalPath}
              onChange={(e) => setJournalPath(e.target.value)}
              placeholder="journals"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Path to your journal files in the repository
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Journal Format
            </label>
            <select
              value={journalFormat}
              onChange={(e) => setJournalFormat(e.target.value as JournalFormat)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            >
              <option value="logseq">LogSeq (YYYY_MM_DD.md)</option>
              <option value="obsidian">Obsidian (YYYY-MM-DD.md)</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!repository || !token}
              isLoading={isSaving}
            >
              Connect Repository
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Placeholder Integration Card Component
function PlaceholderIntegration({ 
  name, 
  description, 
  icon: Icon,
  iconBgClass = 'bg-gray-100',
  iconClass = 'text-gray-400',
}: { 
  name: string; 
  description: string; 
  icon: React.ComponentType<{ className?: string }>;
  iconBgClass?: string;
  iconClass?: string;
}) {
  return (
    <Card className="opacity-60">
      <CardContent>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBgClass}`}>
            <Icon className={`w-6 h-6 ${iconClass}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-500">{name}</h3>
            <p className="text-sm text-gray-400">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Section Header Component
function IntegrationSection({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4 mt-8 first:mt-0">
      <h2 className="text-lg font-bold text-gray-800 border-b border-gray-200 pb-2">{title}</h2>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
  );
}

// Notion Logo
function NotionLogo({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.886l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.448.327s0 .84-1.168.84l-3.22.186c-.094-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.886.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.84.374-1.54 1.447-1.632z"/>
    </svg>
  );
}


// GitHub JIRA Integration Component (Two-Stage Merge Workflow)
function GitHubJiraIntegration() {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [repository, setRepository] = useState('');
  const [githubToken, setGithubToken] = useState('');
  const [branch, setBranch] = useState('main');
  const [jiraFolder, setJiraFolder] = useState('jira-sync');
  const [configId, setConfigId] = useState<string | null>(null);

  const { data: configsData, isLoading: loadingConfigs } = useSpreadsheetConfigs();
  
  // Find GitHub configs
  const githubConfigs = (configsData?.configs || []).filter(
    c => c.fileSource === 'github'
  );
  const hasGitHubConfig = githubConfigs.length > 0;
  const activeConfig = configId 
    ? githubConfigs.find(c => c._id === configId) 
    : githubConfigs[0];

  const { data: syncStatus, isLoading: loadingSyncStatus } = useGitHubSyncStatus(
    activeConfig?._id || ''
  );
  const { data: conflictReport } = useGitHubConflictReport(
    activeConfig?._id || '',
    showReports
  );
  const { data: deltaReport } = useGitHubDeltaReport(
    activeConfig?._id || '',
    showReports
  );

  const { linkGitHub, testConnection, triggerSync, prepareJiraImport } = useGitHubSyncMutations();

  const handleTestConnection = async () => {
    if (!repository || !githubToken) return;
    
    try {
      await testConnection.mutateAsync({
        repository,
        githubToken,
        branch,
        jiraFolder,
      });
    } catch (err) {
      console.error('Connection test failed:', err);
    }
  };

  const handleLinkRepository = async () => {
    if (!repository || !githubToken) return;
    
    try {
      const result = await linkGitHub.mutateAsync({
        repository,
        githubToken,
        branch,
        jiraFolder,
      });
      setConfigId(result.configId);
      setShowSetupDialog(false);
      setGithubToken(''); // Clear token from state for security
    } catch (err) {
      console.error('Link failed:', err);
    }
  };

  const handleSync = async () => {
    if (!activeConfig?._id) return;
    
    try {
      await triggerSync.mutateAsync(activeConfig._id);
    } catch (err) {
      console.error('Sync failed:', err);
    }
  };

  const handleDownloadJiraImport = async () => {
    if (!activeConfig?._id) return;
    
    try {
      const result = await prepareJiraImport.mutateAsync(activeConfig._id);
      if (result.success && result.csvContent) {
        // Download as file
        const blob = new Blob([result.csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || 'jira-import.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  if (loadingConfigs) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-coral-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <GitHubLogo className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">GitHub JIRA Sync</h2>
                <p className="text-sm text-gray-500">
                  Two-stage merge workflow: Export JIRA to GitHub, sync to LifeGuide
                </p>
              </div>
            </div>

            {hasGitHubConfig && (
              <div className="flex items-center gap-2">
                {loadingSyncStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : syncStatus?.exportFile?.exists ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-amber-500">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">No export file</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {!hasGitHubConfig ? (
            // Setup prompt
            <div className="text-center py-8">
              <div className="mb-4">
                <GitHubLogo className="w-12 h-12 text-gray-300 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Connect a GitHub Repository
              </h3>
              <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                Link a GitHub repository to sync JIRA exports. Push jira-export.csv to GitHub,
                then LifeGuide will merge it with the master file and import tasks.
              </p>
              <Button
                onClick={() => setShowSetupDialog(true)}
                className="bg-gray-800 hover:bg-gray-700 text-white"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect Repository
              </Button>
            </div>
          ) : (
            // Connected state
            <div className="space-y-4">
              {/* Repository info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {activeConfig?.githubRepository || syncStatus?.repository}
                    </p>
                    <p className="text-xs text-gray-500">
                      Branch: {activeConfig?.githubBranch || syncStatus?.branch || 'main'} • 
                      Folder: {activeConfig?.githubJiraFolder || 'jira-sync'}
                    </p>
                  </div>
                  <a
                    href={`https://github.com/${activeConfig?.githubRepository || syncStatus?.repository}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* File status */}
              {syncStatus && (
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg ${
                    syncStatus.exportFile?.exists ? 'bg-green-50' : 'bg-amber-50'
                  }`}>
                    <p className="text-xs font-medium text-gray-500">jira-export.csv</p>
                    <p className={`text-sm ${
                      syncStatus.exportFile?.exists ? 'text-green-700' : 'text-amber-700'
                    }`}>
                      {syncStatus.exportFile?.exists ? 'Found' : 'Not found'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${
                    syncStatus.masterFile?.exists ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <p className="text-xs font-medium text-gray-500">master-jira.csv</p>
                    <p className={`text-sm ${
                      syncStatus.masterFile?.exists ? 'text-green-700' : 'text-gray-500'
                    }`}>
                      {syncStatus.masterFile?.exists ? 'Found' : 'Will be created'}
                    </p>
                  </div>
                </div>
              )}

              {/* Last sync info */}
              {activeConfig?.lastSync && (
                <div className="text-xs text-gray-500">
                  Last sync: {new Date(activeConfig.lastSync).toLocaleString()} • 
                  {activeConfig.totalRowsImported || 0} rows imported
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleSync}
                  disabled={triggerSync.isPending || !syncStatus?.exportFile?.exists}
                  className="bg-coral-600 hover:bg-coral-700 text-white"
                >
                  {triggerSync.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync Now
                </Button>

                <Button
                  onClick={() => setShowReports(!showReports)}
                  variant="outline"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {showReports ? 'Hide' : 'View'} Reports
                </Button>

                <Button
                  onClick={handleDownloadJiraImport}
                  disabled={prepareJiraImport.isPending || !syncStatus?.masterFile?.exists}
                  variant="outline"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Export for JIRA
                </Button>
              </div>

              {/* Sync result */}
              {triggerSync.isSuccess && triggerSync.data?.success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Sync completed</span>
                  </div>
                  {triggerSync.data.mergeResult?.stats && (
                    <p className="text-xs text-green-600 mt-1">
                      Added: {triggerSync.data.mergeResult.stats.added} • 
                      Updated: {triggerSync.data.mergeResult.stats.updated} • 
                      Unchanged: {triggerSync.data.mergeResult.stats.unchanged}
                    </p>
                  )}
                </div>
              )}

              {triggerSync.isError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Sync failed</span>
                  </div>
                  <p className="text-xs text-red-600 mt-1">
                    {triggerSync.error instanceof Error ? triggerSync.error.message : 'Unknown error'}
                  </p>
                </div>
              )}

              {/* Reports */}
              {showReports && (
                <div className="space-y-3 pt-3 border-t">
                  <h4 className="text-sm font-medium text-gray-700">Reports</h4>
                  
                  {/* Conflict Report */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-2">Conflict Report</p>
                    {conflictReport?.report ? (
                      <div className="max-h-48 overflow-auto">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                          {conflictReport.report}
                        </pre>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No conflicts detected</p>
                    )}
                  </div>

                  {/* Delta Report */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-2">Delta Report</p>
                    {deltaReport?.report ? (
                      <div className="max-h-48 overflow-auto">
                        <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                          {deltaReport.report}
                        </pre>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">No changes since last sync</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      {showSetupDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <GitHubLogo className="w-6 h-6 text-gray-800" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Connect GitHub Repository</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Repository
                </label>
                <input
                  type="text"
                  value={repository}
                  onChange={(e) => setRepository(e.target.value)}
                  placeholder="owner/repo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Format: username/repository-name
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Access Token
                </label>
                <input
                  type="password"
                  value={githubToken}
                  onChange={(e) => setGithubToken(e.target.value)}
                  placeholder="ghp_xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Create at GitHub → Settings → Developer settings → Personal access tokens
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch
                  </label>
                  <input
                    type="text"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    placeholder="main"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    JIRA Folder
                  </label>
                  <input
                    type="text"
                    value={jiraFolder}
                    onChange={(e) => setJiraFolder(e.target.value)}
                    placeholder="jira-sync"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                </div>
              </div>

              {/* Test connection result */}
              {testConnection.isSuccess && testConnection.data?.success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Connection successful</span>
                  </div>
                  {testConnection.data.files && (
                    <p className="text-xs text-green-600 mt-1">
                      Export file: {testConnection.data.files.exportFile.exists ? '✓ Found' : '✗ Not found'} • 
                      Master file: {testConnection.data.files.masterFile.exists ? '✓ Found' : 'Will be created'}
                    </p>
                  )}
                </div>
              )}

              {testConnection.isSuccess && !testConnection.data?.success && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Connection failed</span>
                  </div>
                  <p className="text-xs text-red-600 mt-1">
                    {testConnection.data?.error || 'Unknown error'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <Button
                onClick={handleTestConnection}
                disabled={testConnection.isPending || !repository || !githubToken}
                variant="outline"
              >
                {testConnection.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Test Connection
              </Button>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowSetupDialog(false);
                    setGithubToken('');
                  }}
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleLinkRepository}
                  disabled={linkGitHub.isPending || !repository || !githubToken}
                  className="bg-coral-600 hover:bg-coral-700 text-white"
                >
                  {linkGitHub.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Connect
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Cloud File Provider type (matches SpreadsheetFileSource)
type CloudProvider = 'google_drive' | 'onedrive';

// Cloud file link configuration
interface CloudFileConfig {
  provider: CloudProvider;
  masterFile?: { id: string; name: string; path: string };
  importFile?: { id: string; name: string; path: string };
}

// Task Files Integration Component (for Jira exports and cloud sync)
function TaskFilesIntegration() {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkDialogStep, setLinkDialogStep] = useState<'provider' | 'master' | 'import'>('provider');
  const [selectedProvider, setSelectedProvider] = useState<CloudProvider | null>(null);
  const [cloudFileConfig, setCloudFileConfig] = useState<CloudFileConfig | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [defaultTopic, setDefaultTopic] = useState('Work');
  const [previewData, setPreviewData] = useState<{
    totalRows: number;
    epics: number;
    stories: number;
    subtasks: number;
    newRows: number;
    existingMappings: number;
  } | null>(null);

  const { data: configsData, isLoading: loadingConfigs } = useSpreadsheetConfigs();
  const { data: status, isLoading: loadingStatus } = useSpreadsheetStatus();
  
  const {
    createConfig,
    uploadFile,
    previewImport,
    deleteConfig,
    triggerSync,
  } = useSpreadsheetImportMutations();

  const configs = configsData?.configs || [];
  const hasConfigs = configs.length > 0;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSelectedFile(file);
    
    // Preview the import
    try {
      const preview = await previewImport.mutateAsync({ file });
      setPreviewData(preview);
    } catch (err) {
      console.error('Preview failed:', err);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      await uploadFile.mutateAsync({
        file: selectedFile,
        options: { defaultProjectTopic: defaultTopic },
      });
      setShowUploadDialog(false);
      setSelectedFile(null);
      setPreviewData(null);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (confirm('Are you sure you want to delete this import configuration? This will not delete imported tasks.')) {
      await deleteConfig.mutateAsync(configId);
    }
  };

  if (loadingConfigs) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-coral-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle provider selection and open file picker
  const handleProviderSelect = (provider: CloudProvider) => {
    setSelectedProvider(provider);
    setLinkDialogStep('master');
  };

  // Simulated file picker for Google Drive
  const openGoogleDrivePicker = (fileType: 'master' | 'import') => {
    // In production, this would use the Google Drive Picker API
    // For now, we'll simulate it with a prompt
    const fileName = prompt(`Enter the ${fileType === 'master' ? 'Master' : 'Import'} file name from Google Drive:`);
    if (fileName) {
      const fileInfo = { id: `gdrive-${Date.now()}`, name: fileName, path: `/My Drive/${fileName}` };
      if (fileType === 'master') {
        setCloudFileConfig(prev => ({
          ...prev,
          provider: 'google_drive',
          masterFile: fileInfo,
        }));
        setLinkDialogStep('import');
      } else {
        setCloudFileConfig(prev => ({
          ...prev!,
          importFile: fileInfo,
        }));
      }
    }
  };

  // Simulated file picker for OneDrive
  const openOneDrivePicker = (fileType: 'master' | 'import') => {
    // In production, this would use the OneDrive File Picker SDK
    // For now, we'll simulate it with a prompt
    const fileName = prompt(`Enter the ${fileType === 'master' ? 'Master' : 'Import'} file name from OneDrive:`);
    if (fileName) {
      const fileInfo = { id: `onedrive-${Date.now()}`, name: fileName, path: `/Documents/${fileName}` };
      if (fileType === 'master') {
        setCloudFileConfig(prev => ({
          ...prev,
          provider: 'onedrive',
          masterFile: fileInfo,
        }));
        setLinkDialogStep('import');
      } else {
        setCloudFileConfig(prev => ({
          ...prev!,
          importFile: fileInfo,
        }));
      }
    }
  };

  const handleCloudFileSave = async () => {
    if (!cloudFileConfig?.masterFile) return;
    
    try {
      // Create config with cloud file info - store file details in name for now
      // Full cloud sync support will be added in a future release
      await createConfig.mutateAsync({
        name: `Cloud: ${cloudFileConfig.masterFile.name}`,
        fileSource: cloudFileConfig.provider,
        defaultProjectTopic: defaultTopic,
      });
      
      setShowLinkDialog(false);
      setLinkDialogStep('provider');
      setSelectedProvider(null);
      setCloudFileConfig(null);
    } catch (err) {
      console.error('Failed to save cloud config:', err);
    }
  };

  const resetLinkDialog = () => {
    setShowLinkDialog(false);
    setLinkDialogStep('provider');
    setSelectedProvider(null);
    setCloudFileConfig(null);
  };

  return (
    <>
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Task Files</h2>
                <p className="text-sm text-gray-500">Import tasks as a one-off or sync bi-directionally with Google Drive or OneDrive files</p>
              </div>
            </div>

            {/* Status indicator */}
            {hasConfigs && (
              <div className="flex items-center gap-2">
                {loadingStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">{status?.totalConfigs || configs.length} config(s)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {!hasConfigs ? (
            // No configurations - show setup prompt
            <div className="text-center py-6">
              <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Import tasks from files or sync with cloud storage.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Upload files for one-off import, or link Google Drive/OneDrive files for bi-directional sync.
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  onClick={() => setShowUploadDialog(true)}
                  leftIcon={<Upload className="w-4 h-4" />}
                >
                  Upload File
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowLinkDialog(true)}
                  leftIcon={<Link2 className="w-4 h-4" />}
                >
                  Link Cloud File
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Existing configurations */}
              <div className="py-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">Import Configurations</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowUploadDialog(true)}
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    New Import
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {configs.map((config: SpreadsheetImportConfig) => (
                    <div
                      key={config._id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{config.name}</p>
                          <p className="text-xs text-gray-500">
                            {config.totalRowsImported} items imported
                            {config.lastSync && (
                              <> · Last sync: {new Date(config.lastSync).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {config.fileSource !== 'upload' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => triggerSync.mutate(config._id)}
                            isLoading={triggerSync.isPending}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteConfig(config._id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick upload button */}
              <div className="pt-3">
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowUploadDialog(true)}
                    leftIcon={<Upload className="w-4 h-4" />}
                  >
                    Upload New File
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLinkDialog(true)}
                    leftIcon={<Link2 className="w-4 h-4" />}
                  >
                    Link Cloud File
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Import Spreadsheet</h3>
            </div>

            <div className="space-y-4">
              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select File
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Supports CSV, Excel (.xlsx, .xls) files from Jira exports
                </p>
              </div>

              {/* Default topic */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Project Topic
                </label>
                <input
                  type="text"
                  value={defaultTopic}
                  onChange={(e) => setDefaultTopic(e.target.value)}
                  placeholder="e.g., Work/ProjectX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Where to place imported items without a parent
                </p>
              </div>

              {/* Preview */}
              {previewData && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">Preview</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-blue-700">Total rows: {previewData.totalRows}</div>
                    <div className="text-blue-700">Epics: {previewData.epics}</div>
                    <div className="text-blue-700">Stories: {previewData.stories}</div>
                    <div className="text-blue-700">Subtasks: {previewData.subtasks}</div>
                    <div className="text-green-700">New: {previewData.newRows}</div>
                    <div className="text-gray-500">Already imported: {previewData.existingMappings}</div>
                  </div>
                </div>
              )}

              {previewImport.isError && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">
                    Failed to preview file. Please check the format.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowUploadDialog(false);
                  setSelectedFile(null);
                  setPreviewData(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile}
                isLoading={uploadFile.isPending}
              >
                Import
              </Button>
            </div>

            {uploadFile.isSuccess && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Import successful!</span>
                </div>
              </div>
            )}

            {uploadFile.isError && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Import failed</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Link Cloud File Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Link2 className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {linkDialogStep === 'provider' && 'Link Cloud Files'}
                {linkDialogStep === 'master' && 'Select Master File'}
                {linkDialogStep === 'import' && 'Select Import File'}
              </h3>
            </div>

            {/* Step 1: Select Provider */}
            {linkDialogStep === 'provider' && (
              <>
                <p className="text-gray-600 mb-4">
                  Link files from Google Drive or OneDrive for bi-directional task synchronization.
                </p>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">How it works:</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Master File:</strong> The source of truth, synced bi-directionally with LifeGuide</li>
                    <li>• <strong>Import File:</strong> Used to merge new tasks into LifeGuide (optional)</li>
                    <li>• <strong>Deltas File:</strong> Auto-generated to track changes between syncs</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    onClick={() => handleProviderSelect('google_drive')}
                    leftIcon={
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                      </svg>
                    }
                  >
                    Google Drive
                  </Button>
                  <Button
                    variant="secondary"
                    className="w-full justify-start"
                    onClick={() => handleProviderSelect('onedrive')}
                    leftIcon={
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10.178 3.5l-5 8.5h5l-5 8.5 12.5-10.5h-7.5l7.5-6.5z"/>
                      </svg>
                    }
                  >
                    OneDrive
                  </Button>
                </div>

                <div className="flex justify-end mt-6">
                  <Button variant="ghost" onClick={resetLinkDialog}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {/* Step 2: Select Master File */}
            {linkDialogStep === 'master' && selectedProvider && (
              <>
                <p className="text-gray-600 mb-4">
                  Select the <strong>Master File</strong> that will be synchronized bi-directionally with LifeGuide. 
                  This file serves as the source of truth.
                </p>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Provider:</strong> {selectedProvider === 'google_drive' ? 'Google Drive' : 'OneDrive'}
                  </p>
                </div>

                <Button
                  className="w-full mb-4"
                  onClick={() => selectedProvider === 'google_drive' ? openGoogleDrivePicker('master') : openOneDrivePicker('master')}
                  leftIcon={
                    selectedProvider === 'google_drive' ? (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10.178 3.5l-5 8.5h5l-5 8.5 12.5-10.5h-7.5l7.5-6.5z"/>
                      </svg>
                    )
                  }
                >
                  Choose Master File from {selectedProvider === 'google_drive' ? 'Google Drive' : 'OneDrive'}
                </Button>

                {cloudFileConfig?.masterFile && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Master file selected</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      {cloudFileConfig.masterFile.name}
                    </p>
                    <p className="text-xs text-green-500">
                      {cloudFileConfig.masterFile.path}
                    </p>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <Button variant="ghost" onClick={() => setLinkDialogStep('provider')}>
                    Back
                  </Button>
                  <Button variant="ghost" onClick={resetLinkDialog}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Select Import File (Optional) */}
            {linkDialogStep === 'import' && selectedProvider && cloudFileConfig?.masterFile && (
              <>
                <p className="text-gray-600 mb-4">
                  Optionally select an <strong>Import File</strong> for merging new tasks into LifeGuide. 
                  This is separate from the master and is used for incoming updates.
                </p>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Master File:</strong> {cloudFileConfig.masterFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    A deltas file will be auto-generated alongside these files to track changes.
                  </p>
                </div>

                <Button
                  variant="secondary"
                  className="w-full mb-4"
                  onClick={() => selectedProvider === 'google_drive' ? openGoogleDrivePicker('import') : openOneDrivePicker('import')}
                  leftIcon={<Upload className="w-4 h-4" />}
                >
                  Choose Import File (Optional)
                </Button>

                {cloudFileConfig?.importFile && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Import file selected</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      {cloudFileConfig.importFile.name}
                    </p>
                  </div>
                )}

                {/* Default Topic */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Project Topic
                  </label>
                  <input
                    type="text"
                    value={defaultTopic}
                    onChange={(e) => setDefaultTopic(e.target.value)}
                    placeholder="e.g., Work/ProjectX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Where to place imported items without a parent
                  </p>
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="ghost" onClick={() => setLinkDialogStep('master')}>
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={resetLinkDialog}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCloudFileSave}
                      isLoading={createConfig.isPending}
                    >
                      Link Files
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Google Calendar Integration Component
function GoogleCalendarIntegration() {
  const { data: config, isLoading: loadingConfig } = useGoogleCalendarConfig();
  const { data: status, isLoading: loadingStatus } = useGoogleCalendarSyncStatus();
  const { data: calendars } = useGoogleCalendars(config?.connected ?? false);

  const { getAuthUrl, handleCallback, updateConfig, testConnection, triggerSync, disconnect } =
    useGoogleCalendarMutations();

  // Handle OAuth callback from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && !handleCallback.isPending && !handleCallback.isSuccess) {
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);

      // Handle the callback
      handleCallback.mutate({ code, state: state || undefined });
    }
  }, [handleCallback]);

  const handleConnect = async () => {
    try {
      const result = await getAuthUrl.mutateAsync();
      // Redirect to Google OAuth
      window.location.href = result.auth_url;
    } catch (e) {
      console.error('Failed to get auth URL:', e);
    }
  };

  const handleDisconnect = async () => {
    if (confirm('Are you sure you want to disconnect Google Calendar?')) {
      await disconnect.mutateAsync();
    }
  };

  const handleToggleSync = async (enabled: boolean) => {
    await updateConfig.mutateAsync({ enabled });
  };

  const handleCalendarChange = async (calendarId: string) => {
    const calendar = calendars?.find((c) => c.id === calendarId);
    await updateConfig.mutateAsync({
      calendarId,
      calendarName: calendar?.name || null,
    });
  };

  const handleSyncDirectionChange = async (syncDirection: 'bidirectional' | 'outbound' | 'inbound') => {
    await updateConfig.mutateAsync({ syncDirection });
  };

  const handleTriggerSync = async () => {
    await triggerSync.mutateAsync('both');
  };

  const handleTestConnection = async () => {
    await testConnection.mutateAsync();
  };

  if (loadingConfig) {
    return (
      <Card className="mb-6">
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-coral-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Google Calendar</h2>
              <p className="text-sm text-gray-500">Sync tasks with Google Calendar</p>
            </div>
          </div>

          {/* Status indicator */}
          {config?.connected && (
            <div className="flex items-center gap-2">
              {loadingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : status?.status === 'ok' ? (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Connected</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-500">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Error</span>
                </div>
              )}
            </div>
          )}
        </div>

        {!config?.connected ? (
          // Not connected - show connect button
          <div className="text-center py-6">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              Connect your Google Calendar to sync tasks with due dates as calendar events.
            </p>
            <Button
              onClick={handleConnect}
              isLoading={getAuthUrl.isPending}
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              Connect Google Calendar
            </Button>
            {handleCallback.isError && (
              <p className="text-sm text-red-500 mt-2">
                Failed to connect: {handleCallback.error instanceof Error ? handleCallback.error.message : 'Unknown error'}
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Connected email */}
            <div className="py-3 border-b border-gray-100">
              <p className="text-sm text-gray-600">
                Connected as: <span className="font-medium">{config.googleEmail}</span>
              </p>
            </div>

            {/* Enable toggle */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Enable Calendar Sync</p>
                <p className="text-sm text-gray-500">
                  Synchronize tasks with Google Calendar
                </p>
              </div>
              <button
                onClick={() => handleToggleSync(!config?.enabled)}
                disabled={updateConfig.isPending}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  config?.enabled ? 'bg-coral-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    config?.enabled ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>

            {config?.enabled && (
              <>
                {/* Calendar Selection */}
                <div className="py-3 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sync Calendar
                  </label>
                  <select
                    value={config?.calendarId || ''}
                    onChange={(e) => handleCalendarChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    disabled={updateConfig.isPending}
                  >
                    <option value="">Select a calendar...</option>
                    {calendars?.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>
                        {calendar.name} {calendar.primary && '(Primary)'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sync Direction */}
                <div className="py-3 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sync Direction
                  </label>
                  <select
                    value={config?.syncDirection || 'bidirectional'}
                    onChange={(e) =>
                      handleSyncDirectionChange(e.target.value as 'bidirectional' | 'outbound' | 'inbound')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                    disabled={updateConfig.isPending}
                  >
                    <option value="bidirectional">Bidirectional (Tasks ↔ Calendar)</option>
                    <option value="outbound">LifeGuide → Calendar only</option>
                    <option value="inbound">Calendar → LifeGuide only</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Bidirectional: Tasks with due dates appear in your calendar, and calendar events create tasks.
                  </p>
                </div>

                {/* Sync Status & Actions */}
                <div className="pt-3">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">{status?.syncedEvents || 0}</span> events synced
                      </p>
                      {config?.lastSync && (
                        <p className="text-xs text-gray-400">
                          Last sync: {new Date(config.lastSync).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleTestConnection}
                      isLoading={testConnection.isPending}
                      leftIcon={<Link2 className="w-4 h-4" />}
                    >
                      Test Connection
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleTriggerSync}
                      isLoading={triggerSync.isPending}
                      leftIcon={<RefreshCw className="w-4 h-4" />}
                    >
                      Sync Now
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDisconnect}
                      isLoading={disconnect.isPending}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      leftIcon={<Power className="w-4 h-4" />}
                    >
                      Disconnect
                    </Button>
                  </div>

                  {testConnection.isSuccess && (
                    <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Connection successful</span>
                      </div>
                      {testConnection.data?.email && (
                        <p className="text-xs text-green-600 mt-1">
                          Connected as: {testConnection.data.email}
                        </p>
                      )}
                    </div>
                  )}

                  {testConnection.isError && (
                    <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Connection failed</span>
                      </div>
                    </div>
                  )}

                  {triggerSync.isSuccess && (
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-2 text-blue-700">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Sync completed</span>
                      </div>
                      {triggerSync.data?.results?.inbound && (
                        <p className="text-xs text-blue-600 mt-1">
                          Created: {triggerSync.data.results.inbound.created || 0}, Updated:{' '}
                          {triggerSync.data.results.inbound.updated || 0}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function IntegrationsPage() {
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [deletingProject, setDeletingProject] = useState<string | null>(null);

  const { data: config, isLoading: loadingConfig, error: configError } = useAsanaConfig();
  const { data: status, isLoading: loadingStatus } = useAsanaSyncStatus();
  const { data: workspaces } = useAsanaWorkspaces(config?.enabled ?? false);
  const { data: projects } = useAsanaProjects(config?.workspaceGid || '');

  // Create a lookup map for project names
  const projectNameMap = new Map(projects?.map((p) => [p.gid, p.name]) || []);

  const { updateConfig, testConnection, triggerSync, saveMapping, deleteMapping } =
    useAsanaConfigMutations();

  const handleToggleSync = async (enabled: boolean) => {
    await updateConfig.mutateAsync({ enabled });
  };

  const handleTestConnection = async () => {
    await testConnection.mutateAsync();
  };

  const handleTriggerSync = async () => {
    await triggerSync.mutateAsync('both');
  };

  const handleAddMapping = async (lifeguideProject: string, asanaProjectGid: string) => {
    await saveMapping.mutateAsync({
      project: lifeguideProject,
      mapping: {
        asanaProjectGid,
        lifeguideProjectName: lifeguideProject,
        sections: {},
      },
    });
    setShowAddMapping(false);
  };

  const handleDeleteMapping = async (project: string) => {
    setDeletingProject(project);
    try {
      await deleteMapping.mutateAsync(project);
    } finally {
      setDeletingProject(null);
    }
  };

  const handleWorkspaceChange = async (workspaceGid: string) => {
    const workspace = workspaces?.find((w) => w.gid === workspaceGid);
    await updateConfig.mutateAsync({
      workspaceGid,
      workspaceName: workspace?.name || null,
    });
  };

  const handleSyncSettingChange = async (
    setting: 'enableOutbound' | 'enableInbound',
    value: boolean
  ) => {
    const currentSettings = config?.syncSettings;
    await updateConfig.mutateAsync({
      syncSettings: {
        enableOutbound: currentSettings?.enableOutbound ?? true,
        enableInbound: currentSettings?.enableInbound ?? true,
        conflictResolution: currentSettings?.conflictResolution ?? 'field_merge',
        pollIntervalMinutes: currentSettings?.pollIntervalMinutes ?? 5,
        [setting]: value,
      },
    });
  };

  if (loadingConfig) {
    return (
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
        </div>
      </div>
    );
  }

  if (configError) {
    const isAuthError = configError instanceof Error && 
      (configError.message.toLowerCase().includes('authentication') || 
       configError.message.toLowerCase().includes('unauthorized') ||
       (configError as { status?: number }).status === 401);
    
    return (
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Failed to load integration settings</p>
            <p className="text-sm text-gray-500 mt-2">
              {isAuthError 
                ? 'Your session has expired. Please refresh the page or log in again.'
                : (configError instanceof Error ? configError.message : 'Unknown error')
              }
            </p>
            {isAuthError && (
              <Button 
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h1>
      <p className="text-gray-500 mb-6">Connect LifeGuide with your favorite tools and services</p>

      {/* ==================== VOICE SECTION ==================== */}
      <IntegrationSection 
        title="🎤 Voice and Transcripts" 
        description="Voice capture, transcription services, and audio imports"
      />
      
      <div className="space-y-4 mb-8">
        <PlaceholderIntegration
          name="Plaud"
          description="Coming soon: Voice memo capture and transcription"
          icon={Mic}
          iconBgClass="bg-purple-100"
          iconClass="text-purple-500"
        />
        
        <PlaceholderIntegration
          name="Otter.ai"
          description="Coming soon: Import meeting transcripts from Otter.ai"
          icon={FileAudio}
          iconBgClass="bg-blue-100"
          iconClass="text-blue-500"
        />
        
        <PlaceholderIntegration
          name="Audio File Import"
          description="Coming soon: Import audio files for transcription"
          icon={Upload}
          iconBgClass="bg-green-100"
          iconClass="text-green-500"
        />
        
        <PlaceholderIntegration
          name="Transcript Import"
          description="Coming soon: Import transcript files from local, Google Drive, or OneDrive"
          icon={FileText}
          iconBgClass="bg-amber-100"
          iconClass="text-amber-500"
        />
      </div>

      {/* ==================== TASKS SECTION ==================== */}
      <IntegrationSection 
        title="✅ Tasks" 
        description="Task management integrations and imports"
      />
      
      <div className="space-y-4 mb-8">
        {/* Asana Integration */}
        <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-coral-100 rounded-lg">
                <AsanaLogo className="w-6 h-6 text-coral-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Asana</h2>
                <p className="text-sm text-gray-500">Sync tasks with Asana</p>
              </div>
            </div>

            {/* Status indicator */}
            {config?.enabled && (
              <div className="flex items-center gap-2">
                {loadingStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : status?.status === 'ok' ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-500">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">Error</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Enable Asana Sync</p>
              <p className="text-sm text-gray-500">
                Synchronize tasks between LifeGuide and Asana
              </p>
            </div>
            <button
              onClick={() => handleToggleSync(!config?.enabled)}
              disabled={updateConfig.isPending}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                config?.enabled ? 'bg-coral-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  config?.enabled ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {config?.enabled && (
            <>
              {/* Workspace Selection */}
              <div className="py-3 border-b border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asana Workspace
                </label>
                <select
                  value={config?.workspaceGid || ''}
                  onChange={(e) => handleWorkspaceChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
                  disabled={updateConfig.isPending}
                >
                  <option value="">Select a workspace...</option>
                  {workspaces?.map((workspace) => (
                    <option key={workspace.gid} value={workspace.gid}>
                      {workspace.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sync Direction Settings */}
              <div className="py-3 border-b border-gray-100 space-y-3">
                <p className="text-sm font-medium text-gray-700">Sync Direction</p>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-900">LifeGuide → Asana</p>
                    <p className="text-xs text-gray-500">
                      Tasks created/updated in LifeGuide sync to Asana
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleSyncSettingChange(
                        'enableOutbound',
                        !config?.syncSettings?.enableOutbound
                      )
                    }
                    disabled={updateConfig.isPending}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      config?.syncSettings?.enableOutbound ? 'bg-coral-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        config?.syncSettings?.enableOutbound ? 'translate-x-4' : ''
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-900">Asana → LifeGuide</p>
                    <p className="text-xs text-gray-500">
                      Tasks created/updated in Asana sync to LifeGuide
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleSyncSettingChange('enableInbound', !config?.syncSettings?.enableInbound)
                    }
                    disabled={updateConfig.isPending}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      config?.syncSettings?.enableInbound ? 'bg-coral-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        config?.syncSettings?.enableInbound ? 'translate-x-4' : ''
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Project Mappings */}
              <div className="py-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Project Mappings</p>
                    <p className="text-xs text-gray-500">
                      Map LifeGuide topics to Asana projects
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAddMapping(true)}
                    disabled={!config?.workspaceGid}
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    Add
                  </Button>
                </div>

                {Object.keys(config?.projectMappings || {}).length === 0 ? (
                  <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                    <Link2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No project mappings configured</p>
                    <p className="text-xs text-gray-400">
                      Add a mapping to sync tasks with Asana
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(config?.projectMappings || {}).map(([project, mapping]) => (
                      <ProjectMappingRow
                        key={project}
                        lifeguideProject={project}
                        asanaProjectGid={mapping.asanaProjectGid}
                        asanaProjectName={projectNameMap.get(mapping.asanaProjectGid)}
                        sections={mapping.sections}
                        onDelete={() => handleDeleteMapping(project)}
                        isDeleting={deletingProject === project}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Sync Status & Actions */}
              <div className="pt-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{status?.syncedTasks || 0}</span> tasks synced
                      across{' '}
                      <span className="font-medium">{status?.syncedProjects || 0}</span> projects
                    </p>
                    {status?.lastPoll && (
                      <p className="text-xs text-gray-400">
                        Last sync: {new Date(status.lastPoll).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleTestConnection}
                    isLoading={testConnection.isPending}
                    leftIcon={<Link2 className="w-4 h-4" />}
                  >
                    Test Connection
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleTriggerSync}
                    isLoading={triggerSync.isPending}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Sync Now
                  </Button>
                </div>

                {testConnection.isSuccess && (
                  <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Connection successful</span>
                    </div>
                    {testConnection.data?.user && (
                      <p className="text-xs text-green-600 mt-1">
                        Connected as: {testConnection.data.user}
                      </p>
                    )}
                  </div>
                )}

                {testConnection.isError && (
                  <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Connection failed</span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      {testConnection.error instanceof Error
                        ? testConnection.error.message
                        : 'Unknown error'}
                    </p>
                  </div>
                )}

                {triggerSync.isSuccess && (
                  <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Sync completed</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Created: {triggerSync.data?.created || 0}, Updated:{' '}
                      {triggerSync.data?.updated || 0}, Errors: {triggerSync.data?.errors || 0}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
        </Card>

        {/* Task Files Integration */}
        <TaskFilesIntegration />

        {/* GitHub JIRA Integration (Two-Stage Merge) */}
        <GitHubJiraIntegration />
      </div>

      {/* ==================== CALENDAR SECTION ==================== */}
      <IntegrationSection 
        title="📅 Calendar" 
        description="Calendar integrations for scheduling and events"
      />
      
      <div className="space-y-4 mb-8">
        <GoogleCalendarIntegration />
      </div>

      {/* ==================== JOURNALING SECTION ==================== */}
      <IntegrationSection 
        title="📓 Journaling" 
        description="Journal and note-taking integrations"
      />
      
      <div className="space-y-4 mb-8">
        {/* GitHub Journal Integration (LogSeq) */}
        <GitHubJournalIntegration />

        <PlaceholderIntegration
          name="Notion"
          description="Coming soon: Sync notes and databases with Notion"
          icon={NotionLogo}
          iconBgClass="bg-gray-100"
          iconClass="text-gray-800"
        />
      </div>

      {/* Add Mapping Dialog */}
      {showAddMapping && (
        <AddMappingDialog
          workspaceGid={config?.workspaceGid || null}
          onAdd={handleAddMapping}
          onCancel={() => setShowAddMapping(false)}
          isAdding={saveMapping.isPending}
        />
      )}
    </div>
  );
}

